import React, { forwardRef } from 'react';
import { ChevronDown } from 'lucide-react';

export type SelectOption = {
  value: string;
  label: string;
};

export type SelectProps = {
  label?: string;
  options: SelectOption[];
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  name?: string;
  placeholder?: string;
  fullWidth?: boolean;
};

const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      options,
      value,
      onChange,
      error,
      disabled = false,
      required = false,
      className = '',
      id,
      name,
      placeholder,
      fullWidth = true,
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    
    const handleClick = (e: React.MouseEvent) => {
      e.stopPropagation();
    };
    
    return (
      <div className={`${fullWidth ? 'w-full' : ''} ${className}`} onClick={handleClick}>
        {label && (
          <label 
            htmlFor={selectId} 
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            {label}
            {required && <span className="text-error-500 ml-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            name={name}
            value={value}
            onChange={onChange}
            disabled={disabled}
            required={required}
            className={`
              flex h-10 w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 pr-8 text-sm 
              ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium 
              placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 
              focus-visible:ring-primary-500 focus-visible:ring-offset-2 
              disabled:cursor-not-allowed disabled:opacity-50 
              dark:border-gray-700 dark:bg-gray-900 dark:ring-offset-gray-950 
              dark:placeholder:text-gray-400 dark:focus-visible:ring-primary-400
              ${error ? 'border-error-500 focus-visible:ring-error-500' : ''}
              ${disabled ? 'bg-gray-100 dark:bg-gray-800' : ''}
            `}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
          </div>
        </div>
        
        {error && (
          <p className="mt-1 text-sm text-error-500">{error}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export default Select;